import { useState } from 'react'
import { useFocusTrap } from '@gjl/ui'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import type { GalleryImage } from '@/data/portfolio'

interface ImageGalleryProps {
  images: GalleryImage[]
  alt: string
}

export function ImageGallery({ images, alt }: ImageGalleryProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  // Trap focus inside the lightbox, close on Esc, restore focus to the trigger.
  // Must run before any early return (rules-of-hooks).
  const lightboxRef = useFocusTrap<HTMLDivElement>(lightboxOpen, { onEscape: () => setLightboxOpen(false) })

  if (!images || images.length === 0) return null

  // Normalize bare strings and {src, thumb} objects. The grid renders `thumb`
  // (a lighter variant) when present; the lightbox always loads full `src`.
  const items = images.map((i) =>
    typeof i === 'string' ? { src: i, thumb: i } : { src: i.src, thumb: i.thumb ?? i.src }
  )

  const openLightbox = (index: number) => {
    setCurrentIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1))
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1))
  }

  // If only one image, show it without gallery controls
  if (items.length === 1) {
    return (
      <div className="relative h-[400px] w-full overflow-hidden rounded-lg md:h-[600px]">
        <img src={items[0].src} alt={alt} className="h-full w-full object-cover" />
      </div>
    )
  }

  return (
    <>
      {/* Gallery Grid */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {items.map((item, index) => (
          <button
            key={index}
            onClick={() => openLightbox(index)}
            className="group relative aspect-video cursor-pointer overflow-hidden rounded-lg transition-opacity hover:opacity-90"
          >
            <img
              src={item.thumb}
              alt={`${alt} - ${index + 1} of ${items.length}`}
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
              <span className="text-sm font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                View Full Size
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          ref={lightboxRef}
          role="dialog"
          aria-modal="true"
          aria-label={`${alt} — image ${currentIndex + 1} of ${items.length}`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label="Close lightbox"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Previous Button */}
          {items.length > 1 && (
            <button
              onClick={goToPrevious}
              className="absolute left-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}

          {/* Image */}
          <div className="relative mx-auto flex h-full max-h-[90vh] w-full max-w-7xl items-center justify-center p-4">
            <img
              src={items[currentIndex].src}
              alt={`${alt} - ${currentIndex + 1} of ${items.length}`}
              className="max-h-full max-w-full object-contain"
            />
          </div>

          {/* Next Button */}
          {items.length > 1 && (
            <button
              onClick={goToNext}
              className="absolute right-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              aria-label="Next image"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}

          {/* Image Counter */}
          {items.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 transform rounded-full bg-white/10 px-4 py-2 text-sm text-white">
              {currentIndex + 1} / {items.length}
            </div>
          )}
        </div>
      )}
    </>
  )
}
